import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { baseUrl } from 'utils/axios'
import { handleErrors, formatParams } from 'utils/helpers'

export interface IFleetMachine {
  id: number
  name: string
  machineModel: { id: number; name: string }
  category: { id: number; name: string; iconName: string | null }
  operator: { id: number; firstName: string; lastName: string }
  location: { id: number; name: string }
  fuelPercentage: number
  healthStatus: number
  availabilityStatus: number
  operationalStatus: number
}

export interface IFleetMachineOverview {
  id: number
  name: string
  machineCategory: { id: number; name: string; iconName: string | null }
  machineModel: { id: number; name: string }
  chassisNumber: string
  yearOfManufacture: number
  purchaseDate: string
  ownership: number
  costPerHour: number
  machineCost: number
  licensePlate: string
  utilizationPercentage: number
  operatingHoursLast30Days: number
  fuelPercentage: number
  operationalStatus: number
  availabilityStatus: number
  healthStatus: number
  operator: { id: number; firstName: string; lastName: string }
  location: { id: number; name: string }
}

export interface IFleetDashboardOverview {
  totalMachineCount: number
  operationalMachineCount: number
  inMaintenanceCount: number
  brokenCount: number
  inUseCount: number
  lowFuelPercentageCount: number
  availabilityPercentage: number
  utilizationPercentage: number
}

export interface IFleetCategory {
  id: number
  name: string
  iconName: string | null
  machineCount: number
}

export interface IFleetCategoryUtilization {
  id: number
  name: string
  iconName: string | null
  utilizationPercentage: number | null
}

interface IFleetMachinesSlice {
  machines: IFleetMachine[]
  totalCount: number
  totalPages: number
  noMachines: boolean
  isLoading: boolean
  overview: IFleetMachineOverview | null
  dashboardOverview: IFleetDashboardOverview | null
  categories: IFleetCategory[]
  categoryUtilization: IFleetCategoryUtilization[]
}

const initialState: IFleetMachinesSlice = {
  machines: [],
  totalCount: 0,
  totalPages: 0,
  noMachines: false,
  isLoading: false,
  overview: null,
  dashboardOverview: null,
  categories: [],
  categoryUtilization: [],
}

export const getFleetMachines = createAsyncThunk(
  'fleetMachines/getAll',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp = await baseUrl.get('machine/dashboard/machine', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFleetMachineOverview = createAsyncThunk(
  'fleetMachines/getOverview',
  async (id: string, thunkAPI) => {
    try {
      const resp = await baseUrl.get(`machine/dashboard/machine/${id}/overview`)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFleetDashboardOverview = createAsyncThunk(
  'fleetMachines/getDashboardOverview',
  async (_, thunkAPI) => {
    try {
      const resp = await baseUrl.get('machine/dashboard/overview')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFleetCategories = createAsyncThunk(
  'fleetMachines/getCategories',
  async (_, thunkAPI) => {
    try {
      const resp = await baseUrl.get('machine/dashboard/category')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFleetCategoryUtilization = createAsyncThunk(
  'fleetMachines/getCategoryUtilization',
  async (_, thunkAPI) => {
    try {
      const resp = await baseUrl.get('machine/dashboard/category/utilization')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const fleetMachinesSlice = createSlice({
  name: 'fleetMachines',
  initialState,
  reducers: {
    clearFleetMachinesList: (state) => {
      state.machines = []
      state.totalCount = 0
      state.totalPages = 0
    },
    clearFleetMachineOverview: (state) => {
      state.overview = null
    },
  },
  extraReducers: {
    [getFleetMachines.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFleetMachines.fulfilled.type]: (state, { payload }) => {
      state.machines = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
      state.noMachines = payload.data.length < 1
      state.isLoading = false
    },
    [getFleetMachines.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFleetMachineOverview.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFleetMachineOverview.fulfilled.type]: (state, { payload }) => {
      state.overview = payload
      state.isLoading = false
    },
    [getFleetMachineOverview.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFleetDashboardOverview.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFleetDashboardOverview.fulfilled.type]: (state, { payload }) => {
      state.dashboardOverview = payload
      state.isLoading = false
    },
    [getFleetDashboardOverview.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFleetCategories.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFleetCategories.fulfilled.type]: (state, { payload }) => {
      state.categories = payload.categories
      state.isLoading = false
    },
    [getFleetCategories.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFleetCategoryUtilization.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFleetCategoryUtilization.fulfilled.type]: (state, { payload }) => {
      state.categoryUtilization = payload.categories
      state.isLoading = false
    },
    [getFleetCategoryUtilization.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearFleetMachinesList, clearFleetMachineOverview } = fleetMachinesSlice.actions
export default fleetMachinesSlice.reducer
